/**
 * Панель фильтров страницы взаимодействий.
 * Дата: 17.03.2026 / polish 17.07.2026
 */
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Badge,
  Button,
  DateRangePicker,
  SearchInput,
  TenantSelector,
} from '@/components/ui';
import {
  interactionSectionRegistry,
  type InteractionSectionId,
} from '@/features/interactions/registry/sectionRegistry';
import type { InteractionDateRange } from '@/features/interactions/hooks/useInteractionFilters';

interface InteractionsFiltersBarProps {
  isSuperAdmin: boolean;
  selectedTenantId: string;
  onTenantChange: (tenantId: string) => void;
  activeSectionId: InteractionSectionId;
  onSectionChange: (sectionId: InteractionSectionId) => void;
  search: string;
  onSearchChange: (value: string) => void;
  dateRange: InteractionDateRange;
  onDateRangeChange: (from: Date | null, to: Date | null) => void;
  datePresets: Array<{ label: string; from: Date; to: Date }>;
  total: number;
}

export function InteractionsFiltersBar({
  isSuperAdmin,
  selectedTenantId,
  onTenantChange,
  activeSectionId,
  onSectionChange,
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  datePresets,
  total,
}: InteractionsFiltersBarProps) {
  return (
    <div className="space-y-2">
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-12">
          {isSuperAdmin && (
            <div className="xl:col-span-2">
              <TenantSelector value={selectedTenantId} onChange={onTenantChange} showLabel={false} />
            </div>
          )}
          <div className={isSuperAdmin ? 'xl:col-span-5' : 'xl:col-span-7'}>
            <SearchInput
              value={search}
              onChange={onSearchChange}
              placeholder="Поиск по пациенту, телефону или callback..."
            />
          </div>
          <div className="xl:col-span-5">
            <div className="rounded-xl border border-shell-border bg-shell-elevated/40 px-3 py-2.5">
              <DateRangePicker
                from={dateRange.from}
                to={dateRange.to}
                presets={datePresets}
                onChange={onDateRangeChange}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1">
            {interactionSectionRegistry.map((section) => (
              <Button
                key={section.id}
                variant={activeSectionId === section.id ? 'primary' : 'ghost'}
                size="sm"
                className="h-8"
                onClick={() => onSectionChange(section.id)}
              >
                {section.label}
              </Button>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-shell-muted">
          <span>
            {dateRange.from && dateRange.to
              ? `${format(dateRange.from, 'd MMMM yyyy', { locale: ru })} — ${format(dateRange.to, 'd MMMM yyyy', { locale: ru })}`
              : 'Все даты'}
          </span>
          <Badge variant="secondary">{total} событий за период</Badge>
        </div>
    </div>
  );
}
